import { useState, useMemo, useCallback } from "react"
import LiteratureReviewLayout from "../LiteratureReviewLayout/LiteratureReviewLayout"
import { DoiButton } from "../DoiButton/DoiButton"
import { downloadCsv } from "../../Utils/exportCsv"
import { MenuBook, KeyboardArrowDown, KeyboardArrowUp, Search, FileUpload, Info, Close, ArrowUpward, ArrowDownward } from "@mui/icons-material"
import "./EvaluateResults.css"

const EvaluateResults = ({
    projectTitle,
    screenedArticles,
    articleUniqueIds,
    articleValues,
    suggestedColumns: suggestedColumnsProp,
    screeningType,
    steps,
    onBack,
    onNext,
    nextLabel,
    showBack = true,
    secondaryButtonLabel,
    onSecondaryClick,
    showSecondary = false,
}) => {
    const [expandedRows, setExpandedRows] = useState({})
    const [expandedAuthors, setExpandedAuthors] = useState({})
    const [scoreThreshold, setScoreThreshold] = useState(3.7)
    const [sortOrder, setSortOrder] = useState("desc")
    const [searchQuery, setSearchQuery] = useState("")
    const [isSearchOpen, setIsSearchOpen] = useState(false)

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const suggestedColumns = useMemo(() =>
        Array.isArray(suggestedColumnsProp) ? suggestedColumnsProp : [],
        [suggestedColumnsProp]
    )

    const isFullText = screeningType === "full-text"
    const judgementLabel = isFullText ? "(Full-text)" : "(Abstract only)"
    const itemLabel = isFullText ? "full-texts" : "abstracts"

    const formatColumnLabel = (key) =>
        (key || "")
            .split(/[-_]/)
            .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
            .join(" ")

    /* ---------- data access helpers ---------- */

    const getRawArticle = (articleId) => {
        if (articleId == null) return null
        return (
            articleValues[articleId] ??
            articleValues[String(articleId)] ??
            articleValues[Number(articleId)] ??
            null
        )
    }

    const getInclusionScore = (articleId) => {
        const raw = getRawArticle(articleId)
        if (!raw) return null
        const score = raw.inclusion_scores ?? raw.inclusionScores ?? raw.inclusionScore
        if (score == null) return null
        if (typeof score === "number") return { value: score, rationale: "" }
        return score
    }

    const getColumnsForArticle = (articleId) => {
        const raw = getRawArticle(articleId)
        if (!raw) return {}
        if (raw.columns && typeof raw.columns === "object") return raw.columns
        const result = {}
        for (const [key, val] of Object.entries(raw)) {
            if (key === "inclusion_scores" || key === "columns") continue
            result[key] = typeof val === "object" ? val : { value: val, rationale: "" }
        }
        return result
    }

    const getColumnValue = (columnsObj, colId) => {
        const col = columnsObj[colId]
        if (!col) return "—"
        return typeof col === "object" ? (col.value ?? "—") : col
    }

    const columnDefs = useMemo(
        () =>
            suggestedColumns
                .map((c) => {
                    const colId = c?.id ?? ""
                    const label = c?.label ?? formatColumnLabel(colId)
                    return { id: colId, label }
                })
                .filter((col) => col.id),
        [suggestedColumns]
    )

    /* ---------- scoring & stats ---------- */

    const getScoreValue = (articleId) => {
        const score = getInclusionScore(articleId)
        if (score == null) return null
        if (typeof score === "number") return score
        return score?.value ?? null
    }

    const maxScore = 10

    const normalizeScore = (value) => {
        if (value == null) return null
        const num = Number(value)
        if (isNaN(num)) return null
        return num
    }

    const getDecision = (articleId) => {
        const normalized = normalizeScore(getScoreValue(articleId))
        if (normalized == null) return "unknown"
        return normalized >= scoreThreshold ? "include" : "exclude"
    }

    const includedIds = useMemo(
        () => articleUniqueIds.filter((aid) => getDecision(aid) === "include"),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [articleUniqueIds, articleValues, scoreThreshold]
    )

    const includedScreenedArticles = useMemo(
        () => includedIds.map((uid) => screenedArticles.find((s) => String(s?.uniqueId) === String(uid))).filter(Boolean),
        [includedIds, screenedArticles]
    )

    const getIncludedArticleValues = () => {
        const vals = {}
        includedIds.forEach((uid) => {
            const raw = getRawArticle(uid)
            if (raw) vals[uid] = raw
        })
        return vals
    }

    const handleNext = () => {
        if (onNext) onNext(includedIds, includedScreenedArticles, getIncludedArticleValues())
    }

    const handleSecondary = () => {
        if (onSecondaryClick) onSecondaryClick(includedIds, includedScreenedArticles, getIncludedArticleValues())
    }

    const stats = useMemo(() => {
        let included = 0
        let excluded = 0
        articleUniqueIds.forEach((aid) => {
            const d = getDecision(aid)
            if (d === "include") included++
            else if (d === "exclude") excluded++
        })
        return { total: articleUniqueIds.length, included, excluded, scored: included + excluded }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [articleUniqueIds, articleValues, scoreThreshold])

    /* ---------- row expand/collapse ---------- */

    const toggleRow = useCallback((articleId) => {
        setExpandedRows((prev) => ({ ...prev, [articleId]: !prev[articleId] }))
    }, [])

    const toggleAuthors = useCallback((articleId) => {
        setExpandedAuthors((prev) => ({ ...prev, [articleId]: !prev[articleId] }))
    }, [])

    /* ---------- threshold handler ---------- */

    const handleThresholdChange = (e) => {
        const val = parseFloat(e.target.value)
        if (!isNaN(val)) setScoreThreshold(Math.min(maxScore, Math.max(0, Math.round(val * 10) / 10)))
    }

    const handleSliderChange = (e) => {
        setScoreThreshold(parseFloat(e.target.value))
    }

    /* ---------- pill helper ---------- */

    const getPillClass = (value) => {
        const lower = String(value).toLowerCase()
        if (lower === "yes") return "pill-yes"
        if (lower === "no") return "pill-no"
        return "pill-maybe"
    }

    const hasResults =
        articleUniqueIds.length > 0 || Object.keys(articleValues).length > 0

    /* ---------- sort & search ---------- */

    /* ---------- CSV export ---------- */

    const handleExport = () => {
        const headers = ["Title", "Authors", "Journal", "Date", "DOI", "Score", "Decision",
            ...columnDefs.map(c => c.label)]
        const rows = displayedArticles.map(({ aid, idx }) => {
            const paper = screenedArticles[idx] ?? {}
            const columns = getColumnsForArticle(aid)
            const score = normalizeScore(getScoreValue(aid))
            const decision = getDecision(aid)
            return [
                paper.title ?? "",
                Array.isArray(paper.authors) ? paper.authors.join("; ") : (paper.authors ?? ""),
                paper.journalTitle ?? "",
                paper.publicationDate ?? "",
                paper.doi ?? "",
                score ?? "",
                decision,
                ...columnDefs.map(col => getColumnValue(columns, col.id))
            ]
        })
        downloadCsv(headers, rows, "screening-results.csv")
    }

    const sortLabel = sortOrder === "desc" ? "Score: High to Low" : "Score: Low to High"

    const toggleSort = () => {
        setSortOrder((prev) => (prev === "desc" ? "asc" : "desc"))
    }

    const displayedArticles = useMemo(() => {
        let pairs = articleUniqueIds.map((aid, idx) => ({ aid, idx }))

        if (searchQuery.trim()) {
            const q = searchQuery.trim().toLowerCase()
            pairs = pairs.filter(({ aid, idx }) => {
                const paper = screenedArticles[idx] ?? {}
                const title = (paper?.title ?? "").toLowerCase()
                const rawAuthors = paper?.authors
                const authors = Array.isArray(rawAuthors) ? rawAuthors.join(", ").toLowerCase() : (rawAuthors ?? "").toLowerCase()
                const abstract = (paper?.abstract ?? "").toLowerCase()
                return title.includes(q) || authors.includes(q) || abstract.includes(q)
            })
        }

        if (sortOrder) {
            pairs.sort((a, b) => {
                const scoreA = normalizeScore(getScoreValue(a.aid)) ?? -1
                const scoreB = normalizeScore(getScoreValue(b.aid)) ?? -1
                return sortOrder === "desc" ? scoreB - scoreA : scoreA - scoreB
            })
        }

        return pairs
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [articleUniqueIds, screenedArticles, articleValues, searchQuery, sortOrder])

    /* ---------- MAIN CONTENT ---------- */

    const mainContent = (
        <div className="eval-screening-content">
            {hasResults && (
                <>
                    {/* Toolbar */}
                    <div className="eval-screening-toolbar">
                        <button className="eval-screening-toolbar-btn" onClick={toggleSort}>
                            {sortOrder === "desc"
                                ? <ArrowDownward sx={{ fontSize: 16 }} />
                                : <ArrowUpward sx={{ fontSize: 16 }} />}
                            {sortLabel}
                        </button>
                        {isSearchOpen ? (
                            <div className="eval-screening-search-wrapper">
                                <Search sx={{ fontSize: 16, color: "#666" }} />
                                <input
                                    type="text"
                                    className="eval-screening-search-input"
                                    placeholder="Search title, author or abstract…"
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    autoFocus
                                />
                                <button
                                    className="eval-screening-search-close"
                                    onClick={() => { setIsSearchOpen(false); setSearchQuery("") }}
                                >
                                    <Close sx={{ fontSize: 16 }} />
                                </button>
                            </div>
                        ) : (
                            <button className="eval-screening-toolbar-btn" onClick={() => setIsSearchOpen(true)}>
                                <Search sx={{ fontSize: 16 }} /> Search
                            </button>
                        )}
                        <button className="eval-screening-toolbar-btn" onClick={handleExport}>
                            <FileUpload sx={{ fontSize: 16 }} /> Export
                        </button>
                    </div>

                    {/* Table */}
                    <div className="eval-screening-table-wrapper">
                        <table className="eval-screening-table">
                            <thead>
                                <tr>
                                    <th className="eval-screening-paper-col">Paper</th>
                                    <th className="eval-screening-judgement-col">
                                        Screening judgement <span className="eval-screening-judgement-label">{judgementLabel}</span>
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {displayedArticles.map(({ aid: articleId, idx: rowIndex }) => {
                                    const paper = screenedArticles.find((s) => String(s?.uniqueId) === String(articleId)) ?? screenedArticles[rowIndex] ?? {}
                                    const inclusion = getInclusionScore(articleId)
                                    const columns = getColumnsForArticle(articleId)
                                    const decision = getDecision(articleId)
                                    const normalizedScore = normalizeScore(getScoreValue(articleId))
                                    const title = paper?.title ?? ""
                                    const rawAuthors = paper?.authors
                                    const authorsList = Array.isArray(rawAuthors) ? rawAuthors : []
                                    const authors = authorsList.length > 0 ? authorsList.join(", ") : (rawAuthors ?? "")
                                    const hasMoreAuthors = authorsList.length > 2
                                    const journal = paper?.journalTitle ?? ""
                                    const publicationDate = paper?.publicationDate ?? ""
                                    const citations = paper?.citationCount ?? 0
                                    const doi = paper?.doi ?? ""
                                    const isExpanded = !!expandedRows[articleId]
                                    const isAuthorsExpanded = !!expandedAuthors[articleId]
                                    const rationale = inclusion?.rationale ?? ""

                                    return (
                                        <tr key={String(articleId)} className={`eval-screening-row ${decision}`}>
                                            {/* Paper cell */}
                                            <td className="eval-screening-paper-cell">
                                                <div className="eval-screening-paper-card">
                                                    <div className="eval-screening-paper-title">{title}</div>
                                                    {authors && (
                                                        <div className="eval-screening-paper-authors-wrapper">
                                                            <div className={`eval-screening-paper-authors ${isAuthorsExpanded ? "expanded" : ""}`}>
                                                                {authors}
                                                            </div>
                                                            {hasMoreAuthors && (
                                                                <button
                                                                    className="eval-screening-authors-toggle"
                                                                    onClick={() => toggleAuthors(articleId)}
                                                                >
                                                                    {isAuthorsExpanded ? "Show less" : "Show all"}
                                                                </button>
                                                            )}
                                                        </div>
                                                    )}
                                                    <div className="eval-screening-paper-meta">
                                                        <span className="paper-meta-item">
                                                            <MenuBook sx={{ fontSize: 14, color: "#666", mr: 0.5, verticalAlign: "middle" }} />
                                                            {journal},
                                                        </span>
                                                        <span className="paper-meta-item">{publicationDate},</span>
                                                        <span className="paper-meta-item">{citations} citation{citations !== 1 ? "s" : ""},</span>
                                                        {doi && <DoiButton doi={doi} />}
                                                    </div>
                                                </div>
                                            </td>

                                            {/* Judgement cell */}
                                            <td className="eval-screening-judgement-cell">
                                                <div className="eval-screening-judgement-content">
                                                    {/* Decision + score row */}
                                                    <div className="eval-screening-judgement-header">
                                                        <span className={`eval-screening-decision-pill decision-${decision}`}>
                                                            <span className="eval-screening-decision-dot" />
                                                            {decision === "include" ? "Include" : decision === "exclude" ? "Exclude" : "Pending"}
                                                        </span>
                                                        {normalizedScore != null && (
                                                            <span className="eval-screening-score">
                                                                {normalizedScore} / {maxScore}
                                                            </span>
                                                        )}
                                                    </div>

                                                    {/* Rationale */}
                                                    {rationale && (
                                                        <p className="eval-screening-rationale">{rationale}</p>
                                                    )}

                                                    {/* Column criteria pills */}
                                                    {columnDefs.length > 0 && (
                                                        <div className="eval-screening-criteria-pills">
                                                            {columnDefs.map((col) => {
                                                                const val = getColumnValue(columns, col.id)
                                                                const pillCls = getPillClass(val)
                                                                return (
                                                                    <span key={col.id} className={`eval-screening-criteria-pill ${pillCls}`}>
                                                                        <span className="eval-screening-criteria-dot" />
                                                                        {col.label}
                                                                    </span>
                                                                )
                                                            })}
                                                        </div>
                                                    )}

                                                    {/* Expand / collapse */}
                                                    {columnDefs.length > 0 && (
                                                        <button
                                                            className="eval-screening-expand-btn"
                                                            onClick={() => toggleRow(articleId)}
                                                        >
                                                            {isExpanded ? "Hide" : "Show"} criteria evaluations
                                                            {isExpanded
                                                                ? <KeyboardArrowUp sx={{ fontSize: 18 }} />
                                                                : <KeyboardArrowDown sx={{ fontSize: 18 }} />}
                                                        </button>
                                                    )}

                                                    {/* Expanded criteria details */}
                                                    {isExpanded && (
                                                        <div className="eval-screening-criteria-details">
                                                            {columnDefs.map((col) => {
                                                                const colData = columns[col.id]
                                                                const val = typeof colData === "object" ? (colData?.value ?? "—") : (colData ?? "—")
                                                                const colRationale = typeof colData === "object" ? (colData?.rationale ?? "") : ""
                                                                const pillCls = getPillClass(val)
                                                                return (
                                                                    <div key={col.id} className="eval-screening-criteria-row">
                                                                        <div className="eval-screening-criteria-label">
                                                                            <span className={`eval-screening-criteria-pill ${pillCls}`}>
                                                                                <span className="eval-screening-criteria-dot" />
                                                                                {col.label}
                                                                            </span>
                                                                            <span className={`eval-screening-criteria-value ${pillCls}`}>{val}</span>
                                                                        </div>
                                                                        {colRationale && (
                                                                            <p className="eval-screening-criteria-rationale">{colRationale}</p>
                                                                        )}
                                                                    </div>
                                                                )
                                                            })}
                                                        </div>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    )
                                })}
                            </tbody>
                        </table>
                    </div>
                </>
            )}
        </div>
    )

    /* ---------- SIDEBAR ---------- */

    const sidebar = (
        <div className="eval-screening-sidebar">
            <div className="eval-screening-sidebar-header">
                <h3 className="eval-screening-sidebar-title">
                    Screening results
                </h3>
                <span className="eval-screening-sidebar-info">
                    <Info sx={{ fontSize: 18, color: "#94a3b8" }} />
                </span>
            </div>

            {/* Overview */}
            <div className="eval-screening-sidebar-section">
                <h4 className="eval-screening-sidebar-subtitle">Overview</h4>
                <ul className="eval-screening-overview-list">
                    <li className="eval-screening-overview-item">
                        <span className="eval-screening-overview-icon eval-check">✓</span>
                        <span>{stats.scored} of {stats.total} {itemLabel} evaluated</span>
                    </li>
                    <li className="eval-screening-overview-item">
                        <span className="eval-screening-overview-dot dot-included" />
                        <span>{stats.included} {itemLabel} included</span>
                    </li>
                    <li className="eval-screening-overview-item">
                        <span className="eval-screening-overview-dot dot-excluded" />
                        <span>{stats.excluded} {itemLabel} excluded</span>
                    </li>
                </ul>
            </div>

            {/* Score threshold */}
            <div className="eval-screening-sidebar-section">
                <h4 className="eval-screening-sidebar-subtitle">
                    Score threshold
                    <span className="eval-screening-sidebar-info-inline">
                        <Info sx={{ fontSize: 16, color: "#94a3b8" }} />
                    </span>
                </h4>
                <div className="eval-screening-threshold-control">
                    <input
                        type="number"
                        className="eval-screening-threshold-input"
                        value={scoreThreshold}
                        min={0}
                        max={maxScore}
                        step={0.1}
                        onChange={handleThresholdChange}
                    />
                    <input
                        type="range"
                        className="eval-screening-threshold-slider"
                        value={scoreThreshold}
                        min={0}
                        max={maxScore}
                        step={0.1}
                        onChange={handleSliderChange}
                        style={{ "--slider-pct": `${(scoreThreshold / maxScore) * 100}%` }}
                    />
                </div>
                <p className="eval-screening-threshold-desc">
                    Papers with screening scores above this value will be included and below this value will be excluded. Papers that fail strict criteria will always be excluded.
                </p>
            </div>
        </div>
    )

    return (
        <LiteratureReviewLayout
            projectTitle={projectTitle || "Evaluate screening results"}
            steps={steps}
            mainContent={mainContent}
            sidebar={sidebar}
            onNext={handleNext}
            onBack={onBack}
            nextLabel={nextLabel}
            showBack={showBack}
            showNext={hasResults}
            secondaryButtonLabel={secondaryButtonLabel}
            onSecondaryClick={showSecondary ? handleSecondary : undefined}
            showSecondary={showSecondary && hasResults}
        />
    )
}

export default EvaluateResults
